import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Button, Alert, Tabs, Tab, Checkbox,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { providersAPI, regionsAPI, backupPricingAPI } from '../../../services/api';
import { Provider, Region, BackupPricingRow } from '../../../types';

interface PriceChangeMap {
  [pricingId: string]: { costPerGB?: number; isAvailable?: boolean };
}

const BackupPricing = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [pricing, setPricing] = useState<BackupPricingRow[]>([]);
  const [selectedProvider, setSelectedProvider] = useState(0);
  const [changes, setChanges] = useState<PriceChangeMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchInitialData = async () => {
    try {
      const providersRes = await providersAPI.getAll();
      setProviders(providersRes.data.filter((p: Provider) => p.IsActive));
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingData = useCallback(async () => {
    if (providers.length === 0) return;
    try {
      const currentProvider = providers[selectedProvider];
      if (!currentProvider) return;
      const [pricingRes, regionsRes] = await Promise.all([
        backupPricingAPI.getVMBackupPricing(currentProvider.ProviderId),
        regionsAPI.getAll(currentProvider.ProviderId),
      ]);
      setPricing(pricingRes.data);
      setRegions(regionsRes.data.filter((r: Region) => r.IsActive));
      setChanges({});
    } catch {
      setError('Failed to load pricing');
    }
  }, [providers, selectedProvider]);

  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => { if (providers.length > 0) fetchPricingData(); }, [selectedProvider, providers, fetchPricingData]);

  const handlePriceChange = (pricingId: number | undefined, field: string, value: number | boolean) => {
    if (pricingId === undefined) return;
    setChanges(prev => ({ ...prev, [pricingId]: { ...prev[pricingId], [field]: value } }));
  };

  const getPriceValue = (regionId: number) => {
    const entry = pricing.find(p => p.RegionId === regionId);
    if (!entry) return '';
    const change = changes[entry.BackupPricingId];
    if (change && 'costPerGB' in change) return change.costPerGB;
    return entry.CostPerGB;
  };

  const getAvailableValue = (regionId: number) => {
    const entry = pricing.find(p => p.RegionId === regionId);
    if (!entry) return false;
    const change = changes[entry.BackupPricingId];
    if (change && 'isAvailable' in change) return change.isAvailable;
    return !!entry.IsAvailable;
  };

  const handleSave = async () => {
    try {
      const updates = Object.entries(changes).map(([pricingId, changedFields]) => {
        const originalPricing = pricing.find(p => p.BackupPricingId === parseInt(pricingId));
        const mergedData = {
          costPerGB: changedFields.costPerGB !== undefined ? changedFields.costPerGB : originalPricing?.CostPerGB ?? 0,
          isAvailable: changedFields.isAvailable !== undefined ? changedFields.isAvailable : originalPricing?.IsAvailable ?? 0,
        };
        return backupPricingAPI.updateVMBackupPrice(pricingId, mergedData);
      });
      await Promise.all(updates);
      setSuccess(`Updated ${Object.keys(changes).length} pricing entries`);
      setTimeout(() => setSuccess(''), 3000);
      fetchPricingData();
    } catch {
      setError('Failed to save pricing');
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">VM Backup Pricing</Typography>
        <Button variant="contained" color="secondary" startIcon={<SaveIcon />} onClick={handleSave} disabled={Object.keys(changes).length === 0}>
          Save Changes {Object.keys(changes).length > 0 && `(${Object.keys(changes).length})`}
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      <Paper>
        <Tabs value={selectedProvider} onChange={(_e, v: number) => setSelectedProvider(v)}>
          {providers.map(provider => (<Tab key={provider.ProviderId} label={provider.ProviderName} />))}
        </Tabs>
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>Cost per GB for VM backups (multiplied by backup frequency)</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Region</TableCell>
                  {regions.map(region => (<TableCell key={region.RegionId} align="center">{region.RegionName}</TableCell>))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Cost per GB</TableCell>
                  {regions.map(region => (
                    <TableCell key={region.RegionId} align="center">
                      <TextField type="number" size="small" value={getPriceValue(region.RegionId)}
                        onChange={(e) => { const v = e.target.value === '' ? 0 : parseFloat(e.target.value); handlePriceChange(pricing.find(p => p.RegionId === region.RegionId)?.BackupPricingId, 'costPerGB', v); }}
                        inputProps={{ step: '0.01', min: '0' }}
                        sx={{ width: '100px', '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5, px: 1 } }} />
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell>Enabled</TableCell>
                  {regions.map(region => (
                    <TableCell key={region.RegionId} align="center">
                      <Checkbox checked={getAvailableValue(region.RegionId)}
                        onChange={(e) => handlePriceChange(pricing.find(p => p.RegionId === region.RegionId)?.BackupPricingId, 'isAvailable', e.target.checked)} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    </Box>
  );
};

export default BackupPricing;